import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BookPageImage from '@/components/feature/BookPageImage';
import ShortcutGrid from '@/pages/home/components/ShortcutGrid';
import { useLastPage } from '@/hooks/useLastPage';
import { TOTAL_PAGES } from '@/lib/book';

const STATS = [
  { value: '148', unit: '쪽', label: '반세기 봉사의 기록' },
  { value: '31', unit: '점', label: '받은 상패와 훈장' },
  { value: '139', unit: '명', label: '이어온 장학생' },
];

export default function Home() {
  const lastPage = useLastPage();

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />

      <main>
        {/* 표지 히어로 */}
        <section className="relative w-full overflow-hidden bg-primary-900">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800" aria-hidden="true"></div>
          <div className="relative mx-auto flex w-full flex-col items-center gap-10 px-4 py-14 md:flex-row md:items-center md:gap-14 md:px-10 md:py-20 lg:py-24">
            <div className="animate-fade-up w-52 shrink-0 sm:w-60 md:w-64 lg:w-72">
              <div className="rounded-lg border border-accent-500/50 bg-background-50 p-2">
                <BookPageImage page={1} className="rounded-md" />
              </div>
              <p className="mt-3 text-center font-label text-sm text-accent-300">표지 · 1쪽</p>
            </div>

            <div className="animate-fade-up w-full max-w-2xl text-center md:text-left">
              <p className="font-label text-sm font-medium tracking-[0.25em] text-accent-300">
                사진 자서전 · {TOTAL_PAGES}쪽
              </p>
              <h1 className="mt-4 font-heading text-4xl font-bold leading-tight text-background-50 md:text-6xl">
                봉사의 길
              </h1>
              <p className="mt-3 font-heading text-xl text-accent-200 md:text-2xl">세창 박종윤 사진 자서전</p>
              <p className="mt-5 text-sm leading-relaxed text-primary-100 md:text-base">
                1969년 뉴대전로타리클럽 창립회원으로 시작한 반세기 봉사의 기록 · {TOTAL_PAGES}쪽
              </p>

              <blockquote className="mx-auto mt-7 max-w-xl border-l-2 border-accent-500 pl-4 text-left md:mx-0">
                <p className="font-heading text-lg leading-relaxed text-background-50 md:text-xl">
                  “내가 태어나서 가장 잘한 일은 로타리에서 봉사한 것입니다.”
                </p>
              </blockquote>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  to="/read?page=1"
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 font-label text-base font-bold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-book-open-line text-lg" aria-hidden="true"></i>
                  처음부터 읽기
                </Link>
                <Link
                  to={`/read?page=${lastPage}`}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300/60 px-6 font-label text-base font-medium text-background-50 transition-colors hover:bg-primary-800"
                >
                  <i className="ri-bookmark-line text-lg" aria-hidden="true"></i>
                  이어서 읽기{lastPage > 1 ? ` · ${lastPage}쪽` : ''}
                </Link>
                <Link
                  to="/read?page=1&toc=1"
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300/60 px-6 font-label text-base font-medium text-background-50 transition-colors hover:bg-primary-800"
                >
                  <i className="ri-list-unordered text-lg" aria-hidden="true"></i>
                  차례 보기
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 통계 */}
        <section className="w-full bg-primary-950">
          <div className="mx-auto grid w-full grid-cols-1 gap-4 px-4 py-10 sm:grid-cols-3 md:gap-6 md:px-10 md:py-12">
            {STATS.map((s) => (
              <div key={s.label} className="flex items-baseline gap-3 rounded-lg border border-primary-800/70 px-5 py-5">
                <span className="font-heading text-3xl font-bold text-accent-400 md:text-4xl">{s.value}</span>
                <span className="font-label text-base text-accent-300">{s.unit}</span>
                <span className="ml-auto font-label text-sm text-primary-200">{s.label}</span>
              </div>
            ))}
          </div>
        </section>

        <ShortcutGrid />

        {/* 소개 티저 */}
        <section className="w-full bg-background-100">
          <div className="mx-auto flex w-full flex-col gap-6 px-4 py-14 md:flex-row md:items-center md:justify-between md:px-10 md:py-16">
            <div className="max-w-2xl">
              <p className="font-label text-sm font-medium tracking-[0.2em] text-accent-700">세창 박종윤</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                1935년생 · 1969.3.5 뉴대전로타리클럽 창립회원
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-secondary-600 md:text-base">
                1989-90 국제로타리 3680지구 총재, (사)한국로타리총재단 의장, 한국로타리장학문화재단 제13대
                이사장을 지내며 반세기 동안 봉사의 자리를 지켜왔습니다.
              </p>
            </div>
            <Link
              to="/about"
              className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-6 font-label text-base font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              약력 자세히 보기
              <i className="ri-arrow-right-line text-lg" aria-hidden="true"></i>
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}